import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { X, Search, Check, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { playerService, searchService } from "../../services/api";
import { scoutAgentService } from "../../services/scoutAgentService";
import { useDebounce } from "../../hooks/useDebounce";
import ScoutAvatar from "./ScoutAvatar";
import { SourceLabelPill } from "./ScoutPills";
import { formatPositionAge, formatScoutHorizon } from "../../utils/scoutFormat";
import { formatScoutPosition } from "../../constants/scoutRequestChoices";
import {
  feeFramePillLabel,
  computeOutsideFrame,
  formatEstValue,
  formatFeeFrame,
} from "../../utils/scoutFeeFrame";

const CAP = 3;

// Three filled/empty dots + text (design 1j/1k/1l). Grey when capped.
const CapStrip = ({ used, capped }) => (
  <div className="flex items-center gap-2">
    <div className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: i < used ? (capped ? "#9CA3AF" : "#2283FF") : "#E5E7EB" }}
        />
      ))}
    </div>
    <span className={`text-[12px] ${capped ? "text-[#6B7280]" : "text-[#6B7280]"}`}>
      {used} of {CAP} recommendation{CAP === 1 ? "" : "s"} used for this request
    </span>
  </div>
);

// One selectable player row (shared by both zones).
const PlayerRow = ({ player, onSelect }) => {
  const meta = [formatScoutPosition(player.position) || player.position, player.age, player.club]
    .filter((v) => v !== null && v !== undefined && v !== "")
    .join(" · ");
  // Zone-1 roster picks carry an accountId (recommendable even without a Wyscout
  // link, thanks to the M3.5 player_account_id path); Zone-2 picks carry a wyscoutId.
  // Only a player with NEITHER is unrecommendable.
  const selectable = !!(player.accountId || player.wyscoutId);
  return (
    <div className="flex items-center gap-3 py-2">
      <ScoutAvatar name={player.name} size={36} />
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold text-[#111827] truncate">{player.name}</p>
        <p className="text-[11.5px] text-[#6B7280] truncate">{meta}</p>
      </div>
      <button
        onClick={() => onSelect(player)}
        disabled={!selectable}
        title={selectable ? undefined : "No account or Wyscout link. This player can't be recommended yet"}
        className="min-h-[44px] px-4 py-1.5 rounded-lg border border-[#E5E7EB] text-[12.5px] font-semibold text-[#111827] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
      >
        Select
      </button>
    </div>
  );
};

const RecommendModal = ({ open, request, onClose, onSent, onViewMyRecs }) => {
  const user = useSelector((state) => state.auth.user);
  const agentName = user?.name || "you";
  const agencyName = user?.agency_name || "your agency";

  const [used, setUsed] = useState(0);

  const [rosterQuery, setRosterQuery] = useState("");
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const debouncedRoster = useDebounce(rosterQuery, 350);

  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedSearch = useDebounce(searchQuery, 350);

  const [picked, setPicked] = useState(null); // normalized player | null
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const searchAbort = useRef(null);
  const rosterAbort = useRef(null);

  const capped = used >= CAP;
  const club = request?.club?.name || "the club";
  const frameBody = formatFeeFrame(request?.fee_frame);

  // ── cap: the OWN per-request count now rides on the request payload
  // (`my_recommendations_used`, added to the feed + detail serializer in the M3.5
  // BE follow-up) — no more deriving it from the paginated my-recs list (review
  // N-5). Own-data only (never another agent's). The server still enforces the real
  // cap on submit (400 cap_reached handled). ──
  useEffect(() => {
    if (!open || !request) return;
    setPicked(null);
    setReason("");
    setSent(false);
    setRosterQuery("");
    setSearchQuery("");
    setResults([]);
    setUsed(Math.min(request.my_recommendations_used ?? 0, CAP));
  }, [open, request]);

  // Zone 1 — own roster (agency player list).
  useEffect(() => {
    if (!open || capped || picked || sent) return;
    let active = true;
    setRosterLoading(true);
    if (rosterAbort.current) rosterAbort.current.abort();
    const controller = new AbortController();
    rosterAbort.current = controller;
    const params = new URLSearchParams({ page: "1" });
    if (debouncedRoster) params.append("search", debouncedRoster);
    playerService
      .getPlayers(params, controller.signal)
      .then((res) => {
        if (!active) return;
        if (res.success) {
          setRoster(
            res.data.map((p) => ({
              key: `roster-${p.account_id || p.wyscout_id}`,
              accountId: p.account_id || null, // Zone-1 identifier (preferred on submit)
              wyscoutId: p.wyscout_id || null,
              name: p.name,
              position: p.position,
              age: p.age,
              club: p.current_club_name,
              source: "agency", // Zone 1 ⊆ agency_player (D-007)
              marketValue: null,
              marketCurrency: null,
            })),
          );
        }
      })
      .catch(() => {})
      .finally(() => active && setRosterLoading(false));
    return () => {
      active = false;
    };
  }, [open, capped, picked, sent, debouncedRoster]);

  // Zone 2 — any platform player (Wyscout search). Only fires with a query.
  useEffect(() => {
    if (!open || capped || picked || sent) return;
    if (!debouncedSearch) {
      setResults([]);
      return;
    }
    let active = true;
    setSearchLoading(true);
    if (searchAbort.current) searchAbort.current.abort();
    const controller = new AbortController();
    searchAbort.current = controller;
    searchService
      .search({ type: "players", query: debouncedSearch, page: 1, per_page: 15 }, controller.signal)
      .then((res) => {
        if (!active) return;
        const players = res?.data?.results?.players || [];
        setResults(
          players.map((p) => ({
            key: `ws-${p.wyscout_id}`,
            accountId: null, // Zone-2 picks are keyed by wyscout_player
            wyscoutId: p.wyscout_id || null,
            name: p.short_name || p.name,
            position: p.position,
            age: p.age,
            club: p.club?.name,
            source: null, // computed server-side; unknown pre-send for a search pick
            marketValue: p.market_value ?? null,
            marketCurrency: p.market_currency || null,
          })),
        );
      })
      .catch(() => {})
      .finally(() => active && setSearchLoading(false));
    return () => {
      active = false;
    };
  }, [open, capped, picked, sent, debouncedSearch]);

  if (!open || !request) return null;

  const outside = picked ? computeOutsideFrame(request.fee_frame, picked.marketValue, picked.marketCurrency) : false;
  const estValue = picked ? formatEstValue(picked.marketValue, picked.marketCurrency) : "";

  const submit = async () => {
    if (!picked || !(picked.accountId || picked.wyscoutId) || !reason.trim() || sending) return;
    setSending(true);
    // Zone-1 roster pick → player_account_id; Zone-2 search pick → wyscout_player.
    const res = await scoutAgentService.recommend({
      requestId: request.id,
      playerAccountId: picked.accountId || undefined,
      wyscoutPlayer: picked.accountId ? undefined : picked.wyscoutId,
      reason: reason.trim(),
    });
    setSending(false);
    if (res.success) {
      setUsed(res.data.used ?? used + 1);
      setSent(true);
      onSent?.();
    } else if (res.data?.cap_reached) {
      setUsed(res.data.used ?? CAP);
      toast("You've used all 3 recommendations on this request.");
    } else {
      toast.error(res.error || "Could not send the recommendation.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[rgba(17,24,39,0.35)] p-0 sm:p-4">
      <div
        data-testid="recommend-modal"
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-[620px] max-h-[92vh] sm:max-h-[90vh] flex flex-col shadow-[0_-8px_30px_rgba(17,24,39,0.18)] sm:shadow-[0_8px_30px_rgba(17,24,39,0.18)]"
      >
        {/* Bottom-sheet grab handle (mobile only, design 1u) */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1" aria-hidden="true">
          <span className="w-9 h-1 rounded-full bg-[#E5E7EB]" />
        </div>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-[#E5E7EB]">
          <div className="min-w-0">
            <h2 className="text-[17px] font-bold text-[#111827]">Recommend a player</h2>
            <p className="text-[12.5px] text-[#6B7280] mt-0.5 truncate">
              {request.club?.name} · {formatPositionAge(request)} · {formatScoutHorizon(request.horizon)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {frameBody && (
              <span className="hidden sm:inline-flex items-center rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-[11.5px] font-medium text-[#374151]">
                Fee frame: {frameBody}
              </span>
            )}
            <button onClick={onClose} className="p-1 text-[#9CA3AF] hover:text-[#111827]" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Cap strip */}
        {!sent && (
          <div className="px-5 py-3 border-b border-[#F3F4F6]">
            <CapStrip used={used} capped={capped} />
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {sent ? (
            // 1m — sent confirmation
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-14 h-14 rounded-full bg-[#ECFDF5] flex items-center justify-center mb-4">
                <Check className="w-7 h-7 text-[#16A34A]" />
              </div>
              <h3 className="text-[17px] font-bold text-[#111827] mb-1.5">Recommendation sent</h3>
              <p className="text-[13px] text-[#6B7280] max-w-sm">
                {request.club?.name} will review it privately. If they add {picked?.name} to their radar,
                you&apos;ll see &ldquo;On the radar&rdquo; in My recommendations, with your name attached for good.
              </p>
            </div>
          ) : capped ? (
            // 1l — capped
            <div className="bg-[#F3F4F6] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#9CA3AF]" />
                ))}
              </div>
              <p className="text-[14px] font-semibold text-[#111827]">3 of 3 recommendations used for this request</p>
              <p className="text-[12.5px] text-[#6B7280] mt-1.5">
                The cap keeps recommendations worth reading. You can still recommend players for any other
                scout request, and you&apos;ll see &ldquo;On the radar&rdquo; here if {request.club?.name} picks
                one of yours up.
              </p>
            </div>
          ) : picked ? (
            // 1k / 2c — picked
            <div>
              <div className="flex items-center gap-3 p-3 rounded-xl border border-[#E5E7EB]">
                <ScoutAvatar name={picked.name} size={40} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[14px] font-semibold text-[#111827]">{picked.name}</p>
                    {picked.source === "agency" && <SourceLabelPill label="agency_player" />}
                  </div>
                  <p className="text-[11.5px] text-[#6B7280] mt-0.5">
                    {[formatScoutPosition(picked.position) || picked.position, picked.age, picked.club, estValue]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <button
                  onClick={() => setPicked(null)}
                  className="text-[12.5px] font-semibold text-[#1B6FE0] hover:underline flex-shrink-0"
                >
                  Change
                </button>
              </div>

              {/* 2c — outside fee frame, non-blocking */}
              {outside && (
                <div className="mt-4 rounded-xl bg-[#FEF3C7] p-4" data-testid="outside-frame-warning">
                  <div className="flex items-center gap-2 text-[#D97706]">
                    <AlertTriangle className="w-4 h-4" />
                    <p className="text-[13px] font-semibold">Outside the club&apos;s fee frame.</p>
                  </div>
                  <p className="text-[12.5px] text-[#92610A] mt-1.5">
                    {request.club?.name} set &ldquo;{frameBody}&rdquo;. You can still send this, and the club
                    will see it marked accordingly. A short word on why it&apos;s realistic helps.
                  </p>
                </div>
              )}

              {/* Reason */}
              <div className="mt-4">
                <label className="block text-[13px] font-semibold text-[#111827] mb-1.5">
                  Why should they watch him?
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 140))}
                  rows={3}
                  placeholder="One honest line the club will actually read."
                  className="w-full bg-[#F3F4F6] rounded-lg px-3 py-2.5 text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25 resize-none"
                />
                <div className="flex justify-end mt-1">
                  <span className="text-[11px] text-[#9CA3AF]">{reason.length} / 140</span>
                </div>
                <p className="text-[11.5px] text-[#9CA3AF] mt-1">
                  Sent as {agentName} · {agencyName}, visible only to {club}.
                </p>
              </div>
            </div>
          ) : (
            // 1j — zones
            <div>
              {/* Zone 1 — your players */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[13px] font-semibold text-[#111827]">Your players</p>
                  <p className="text-[11.5px] text-[#9CA3AF]">
                    {agencyName} · {roster.length} player{roster.length === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="relative mb-2">
                  <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    value={rosterQuery}
                    onChange={(e) => setRosterQuery(e.target.value)}
                    placeholder="Search your roster…"
                    className="w-full bg-[#F3F4F6] rounded-lg pl-9 pr-3 py-2 text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
                  />
                </div>
                <div className="divide-y divide-[#F3F4F6]">
                  {rosterLoading ? (
                    <p className="text-[12.5px] text-[#9CA3AF] py-3">Loading your roster…</p>
                  ) : roster.length === 0 ? (
                    <p className="text-[12.5px] text-[#9CA3AF] py-3">No roster players match.</p>
                  ) : (
                    roster.map((p) => <PlayerRow key={p.key} player={p} onSelect={setPicked} />)
                  )}
                </div>
              </div>

              <div className="my-4 border-t border-[#E5E7EB]" />

              {/* Zone 2 — recommend another player */}
              <div>
                <p className="text-[13px] font-semibold text-[#111827]">Recommend another player</p>
                <p className="text-[11.5px] text-[#6B7280] mt-0.5 mb-2">
                  Any player on TransferPitch. The club always sees whether he&apos;s at your agency.
                </p>
                <div className="relative mb-2">
                  <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search all players…"
                    className="w-full bg-[#F3F4F6] rounded-lg pl-9 pr-3 py-2 text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
                  />
                </div>
                <div className="divide-y divide-[#F3F4F6]">
                  {searchLoading ? (
                    <p className="text-[12.5px] text-[#9CA3AF] py-3">Searching…</p>
                  ) : debouncedSearch && results.length === 0 ? (
                    <p className="text-[12.5px] text-[#9CA3AF] py-3">No players match.</p>
                  ) : (
                    results.map((p) => <PlayerRow key={p.key} player={p} onSelect={setPicked} />)
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-5 border-t border-[#E5E7EB]">
          {sent ? (
            <div className="flex items-center justify-end gap-3 w-full">
              <button
                onClick={onViewMyRecs}
                className="px-4 py-2 rounded-lg border border-[#E5E7EB] text-[13px] font-semibold text-[#111827] hover:bg-gray-50"
              >
                View my recommendations
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0]"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: i < used ? (capped ? "#9CA3AF" : "#2283FF") : "#E5E7EB" }}
                  />
                ))}
                <span className="text-[12px] text-[#6B7280] ml-1">
                  {used} of {CAP} used
                </span>
              </div>
              <button
                onClick={submit}
                disabled={capped || !picked || !reason.trim() || sending}
                className="min-h-[44px] px-4 py-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold hover:bg-[#1B6FE0] disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed whitespace-nowrap"
              >
                {sending ? "Sending…" : "Send recommendation"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecommendModal;
